
import React, { useState, useEffect, useRef } from 'react';
import { X, Play, CheckCircle2, AlertTriangle, Loader2, DollarSign } from 'lucide-react';
import { api } from '../../services/api';

interface GenerateModalProps {
    isOpen: boolean;
    onClose: () => void;
    entityId: string;
    projectId: string;
    onSuccess: () => void;
}

type Step = 'config' | 'preview' | 'running' | 'success';

const GenerateModal: React.FC<GenerateModalProps> = ({ isOpen, onClose, entityId, projectId, onSuccess }) => {
    const [step, setStep] = useState<Step>('config');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Config State
    const [quantity, setQuantity] = useState(5);
    const [format, setFormat] = useState('manual');
    const [mode, setMode] = useState('comprehensive');

    // Preview State
    const [previewData, setPreviewData] = useState<any>(null);

    // Running State
    const [runId, setRunId] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState<any[]>([]);
    const logsEndRef = useRef<HTMLDivElement>(null);

    // Reset on open
    useEffect(() => {
        if (isOpen) {
            setStep('config');
            setLoading(false);
            setError(null);
            setRunId(null);
            setProgress(0);
            setLogs([]);
        }
    }, [isOpen]);

    // Auto-scroll logs
    useEffect(() => {
        logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    // Poll run status
    useEffect(() => {
        let interval: any;
        if (step === 'running' && runId) {
            interval = setInterval(async () => {
                try {
                    const data = await api.getGenerationRun(runId);
                    setProgress(data.progress || 0);
                    setLogs(data.logs || []);

                    if (data.status === 'COMPLETED') {
                        setStep('success');
                        clearInterval(interval);
                        onSuccess(); // Refresh parent
                    } else if (data.status === 'FAILED') {
                        setError(data.error || 'Generación fallida');
                        clearInterval(interval);
                    }
                } catch (e) {
                    console.error("Error polling run:", e);
                }
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, runId]);

    const handlePreview = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.getGenerationPreview({
                hu_id: entityId,
                project_id: projectId,
                quantity,
                mode
            });
            setPreviewData(data);
            setStep('preview');
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error calculando preview');
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.startGenerationRun({
                hu_id: entityId,
                project_id: projectId,
                output_format: format,
                test_type: 'funcional', // Default
                max_cases: quantity,
                // mode logic if needed
            });
            setRunId(data.run_id);
            setStep('running');
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error iniciando generación');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center bg-slate-800/50">
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                        <Play className="w-5 h-5 text-primary" />
                        Generar Casos de Prueba
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            {error}
                        </div>
                    )}

                    {step === 'config' && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Cantidad de Casos</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[5, 10, 15, 20].map(n => (
                                        <button
                                            key={n}
                                            onClick={() => setQuantity(n)}
                                            className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${quantity === n
                                                ? 'bg-primary text-white border-primary'
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                                                }`}
                                        >
                                            {n} Casos
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Formato de Salida</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => setFormat('manual')}
                                        className={`p-3 rounded-lg border text-left transition-colors ${format === 'manual' ? 'bg-primary/10 border-primary' : 'bg-slate-800 border-slate-700'
                                            }`}
                                    >
                                        <div className={`font-medium ${format === 'manual' ? 'text-primary' : 'text-slate-300'}`}>Excel (Manual)</div>
                                        <div className="text-xs text-slate-500 mt-1">Spreadsheet tradicional con pasos detallados</div>
                                    </button>
                                    <button
                                        onClick={() => setFormat('gherkin')}
                                        className={`p-3 rounded-lg border text-left transition-colors ${format === 'gherkin' ? 'bg-primary/10 border-primary' : 'bg-slate-800 border-slate-700'
                                            }`}
                                    >
                                        <div className={`font-medium ${format === 'gherkin' ? 'text-primary' : 'text-slate-300'}`}>Gherkin (BDD)</div>
                                        <div className="text-xs text-slate-500 mt-1">Given-When-Then formato compatible con Cucumber</div>
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Estrategia</label>
                                <select
                                    value={mode}
                                    onChange={(e) => setMode(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-primary"
                                >
                                    <option value="comprehensive">Comprehensive (Happy Path + Negativos + Bordes)</option>
                                    <option value="focused">Focused (Solo Critical Path)</option>
                                    <option value="security">Security Oriented</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {step === 'preview' && previewData && (
                        <div className="space-y-6">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h3 className="text-sm font-medium text-slate-400 mb-4">Resumen de Estimación</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-900 p-3 rounded border border-slate-800">
                                        <div className="text-xs text-slate-500 mb-1">Costo Estimado</div>
                                        <div className="text-xl font-bold text-white flex items-center gap-1">
                                            <DollarSign className="w-4 h-4 text-emerald-400" />
                                            {previewData.estimated_cost} USD
                                        </div>
                                    </div>
                                    <div className="bg-slate-900 p-3 rounded border border-slate-800">
                                        <div className="text-xs text-slate-500 mb-1">Tokens Totales</div>
                                        <div className="text-xl font-bold text-white">
                                            {previewData.estimated_tokens.toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={`p-4 rounded-lg border ${previewData.quality_gate_passed
                                ? 'bg-emerald-500/10 border-emerald-500/20'
                                : 'bg-amber-500/10 border-amber-500/20'
                                }`}>
                                <div className="flex items-center gap-2 mb-2">
                                    {previewData.quality_gate_passed
                                        ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                        : <AlertTriangle className="w-5 h-5 text-amber-400" />
                                    }
                                    <h3 className={`font-medium ${previewData.quality_gate_passed ? 'text-emerald-400' : 'text-amber-400'
                                        }`}>
                                        Quality Gate: {previewData.quality_gate_passed ? 'Aprobado' : 'Advertencia'}
                                    </h3>
                                </div>
                                {!previewData.quality_gate_passed && (
                                    <ul className="list-disc list-inside text-sm text-amber-300/80 pl-2">
                                        {previewData.quality_issues.map((issue: string, i: number) => (
                                            <li key={i}>{issue}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 'running' && (
                        <div className="space-y-6">
                            <div className="text-center py-4">
                                <div className="text-4xl font-bold text-primary mb-2">{progress}%</div>
                                <div className="text-slate-400 text-sm">Progreso General</div>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-primary h-full transition-all duration-500 ease-out"
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>

                            {/* Terminal Logs */}
                            <div className="bg-black/50 rounded-lg border border-slate-800 p-4 font-mono text-xs h-[200px] overflow-y-auto">
                                {logs.map((log, i) => (
                                    <div key={i} className="mb-1">
                                        <span className="text-slate-500">[{new Date(log.ts).toLocaleTimeString()}]</span>{' '}
                                        <span className="text-slate-300">{log.msg}</span>
                                    </div>
                                ))}
                                <div ref={logsEndRef} />
                            </div>
                        </div>
                    )}

                    {step === 'success' && (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">¡Generación Exitosa!</h3>
                            <p className="text-slate-400 mb-6">
                                Se han generado correctamente los casos de prueba solicitados.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700 flex justify-end gap-3">
                    {step === 'config' && (
                        <>
                            <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:text-white transition-colors">Cancelar</button>
                            <button
                                onClick={handlePreview}
                                disabled={loading}
                                className="px-4 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg flex items-center gap-2"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Continuar a Preview
                            </button>
                        </>
                    )}

                    {step === 'preview' && (
                        <>
                            <button onClick={() => setStep('config')} className="px-4 py-2 text-slate-300 hover:text-white transition-colors">Atrás</button>
                            <button
                                onClick={handleStart}
                                disabled={loading}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-2"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Iniciar Generación
                            </button>
                        </>
                    )}

                    {step === 'running' && (
                        <button disabled className="px-4 py-2 text-slate-500 cursor-not-allowed">
                            Procesando...
                        </button>
                    )}

                    {step === 'success' && (
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg"
                        >
                            Ver Resultados
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GenerateModal;
